import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, QueryStatus, useToast } from "@cx/ui";
import type { WhatsappLinkCreated, WhatsappLinkStatus, WhatsappSandboxMessage } from "@cx/contracts";
import { formatBrPhone } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";

/**
 * WhatsApp de consultas (RF-BI-13): o usuário cadastra o próprio celular, envia o código mostrado aqui do WhatsApp
 * dele para o número do CX ERP e passa a perguntar por lá (mesmas permissões do Copiloto). No ambiente de testes, um
 * simulador faz o papel do celular.
 */
export function WhatsappPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [created, setCreated] = useState<WhatsappLinkCreated | null>(null);
  const [removing, setRemoving] = useState(false);
  const status = useQuery({
    queryKey: ["whatsapp-link", companyId],
    queryFn: () => api<WhatsappLinkStatus>("/whatsapp/link"),
  });
  const st = status.data;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["whatsapp-link", companyId] });

  const register = useMutation({
    mutationFn: (p: string) =>
      api<WhatsappLinkCreated>("/whatsapp/link", { method: "POST", body: JSON.stringify({ phone: p }) }),
    onSuccess: (r) => {
      setCreated(r);
      setPhoneError(undefined);
      refresh();
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields?.phone) setPhoneError(e.fields.phone);
      else toast("danger", errorMessage(e));
    },
  });
  const remove = useMutation({
    mutationFn: () => api("/whatsapp/link", { method: "DELETE" }),
    onSuccess: () => {
      setCreated(null);
      toast("success", "Número removido");
      refresh();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });

  const companyName = (id: string | null) => {
    const c = me.companies.find((x) => x.id === id);
    return c ? (c.tradeName ?? c.legalName) : "—";
  };
  const bot = st?.botNumber ? formatBrPhone(st.botNumber) : "o número do CX ERP";

  return (
    <>
      <ContentHeader title="WhatsApp" breadcrumb={["Meu Negócio", "WhatsApp"]} />
      <QueryStatus loading={status.isLoading} error={status.error} />
      {st?.provider === "disabled" && (
        <div className="alert alert-info">O WhatsApp do CX ERP ainda não está disponível neste ambiente.</div>
      )}
      {st && st.provider !== "disabled" && (
        <div className="row g-3">
          <div className="col-lg-6">
            <div className="box card h-100">
              <div className="card-body">
                <h2 className="h5">Acompanhe pelo WhatsApp</h2>
                <p className="text-muted">
                  Pergunte pelo WhatsApp o que perguntaria ao Copiloto (vendas, produtos, estoque, clientes e
                  contas). As respostas usam as suas permissões e a empresa ativa no cadastro.
                </p>
                {st.link ? (
                  <div className="mb-3">
                    <div>
                      Número cadastrado: <strong>{formatBrPhone(st.link.phone)}</strong>
                    </div>
                    <div className="small text-muted">
                      Empresa: {companyName(st.link.companyId)}
                      {st.link.verifiedAt && ` · confirmado em ${dateTime(st.link.verifiedAt)}`}
                    </div>
                    <div className="mt-2">
                      Mande suas perguntas para <strong>{bot}</strong>.
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm mt-3"
                      onClick={() => setRemoving(true)}
                    >
                      Remover número
                    </button>
                  </div>
                ) : null}
                <form
                  className="row"
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    register.mutate(phone);
                  }}
                >
                  <Field
                    label={st.link ? "Trocar o número" : "Seu celular com DDD"}
                    required
                    error={phoneError}
                    hint="Ex.: (11) 98765-4321"
                    className="col-md-8"
                  >
                    <input
                      className="form-control"
                      inputMode="tel"
                      autoComplete="tel-national"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </Field>
                  <div className="col-md-4 d-flex align-items-start pt-md-4 mt-md-2">
                    <button type="submit" className="btn btn-primary" disabled={register.isPending}>
                      Gerar código
                    </button>
                  </div>
                </form>
                {(created ?? st.pending) && (
                  <div className="alert alert-warning mb-0" role="status">
                    {created ? (
                      <>
                        Envie o código <strong className="fs-5 font-monospace">{created.code}</strong> do
                        WhatsApp do número {formatBrPhone(created.phone)} para {bot}. O código vale até{" "}
                        {dateTime(created.expiresAt)}.
                      </>
                    ) : (
                      <>
                        Aguardando o código do número {formatBrPhone(st.pending!.phone)} (vale até{" "}
                        {dateTime(st.pending!.expiresAt)}). Perdeu o código? Gere outro.
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {st.provider === "sandbox" && (st.link || st.pending) && (
            <div className="col-lg-6">
              <SandboxSimulator onActivity={refresh} />
            </div>
          )}
        </div>
      )}
      <ConfirmDialog
        open={removing}
        title="Remover número"
        message="O número deixa de receber respostas do CX ERP. Você pode cadastrá-lo de novo depois."
        confirmLabel="Remover"
        onConfirm={() => {
          setRemoving(false);
          remove.mutate();
        }}
        onCancel={() => setRemoving(false)}
      />
    </>
  );
}

/** Simulador do celular (só no provedor de testes): envia como se fosse o seu WhatsApp e mostra as respostas. */
function SandboxSimulator({ onActivity }: { onActivity(): void }): ReactElement {
  const { companyId, api } = useSession();
  const toast = useToast();
  const [text, setText] = useState("");
  const [sent, setSent] = useState<{ text: string; at: string }[]>([]);
  const msgs = useQuery({
    queryKey: ["whatsapp-sandbox", companyId],
    queryFn: () =>
      api<{ phone: string | null; data: WhatsappSandboxMessage[] }>("/whatsapp/sandbox/messages"),
    refetchInterval: 1500,
  });
  const send = useMutation({
    mutationFn: (t: string) =>
      api("/whatsapp/sandbox/inbound", { method: "POST", body: JSON.stringify({ text: t }) }),
    onSuccess: (_r, t) => {
      setSent((s) => [...s, { text: t, at: new Date().toISOString() }]);
      setText("");
      window.setTimeout(onActivity, 1500);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const all = [
    ...sent.map((m) => ({ ...m, mine: true })),
    ...(msgs.data?.data ?? []).map((m) => ({ text: m.text, at: m.at, mine: false })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  return (
    <div className="box card h-100">
      <div className="card-body d-flex flex-column">
        <h2 className="h5">Simulador do WhatsApp</h2>
        <p className="small text-muted">Ambiente de testes: nada sai para o WhatsApp de verdade.</p>
        <div
          className="flex-grow-1 border rounded p-2 mb-2 overflow-auto"
          style={{ maxHeight: 320 }}
          role="log"
          aria-live="polite"
          aria-label="Conversa no WhatsApp"
          // Região que rola: focável para quem usa só o teclado (axe scrollable-region-focusable).
          tabIndex={0}
        >
          {all.length === 0 && <p className="text-muted small mb-0">Nenhuma mensagem ainda.</p>}
          {all.map((m, i) => (
            <div
              key={`${m.at}-${i}`}
              className={`copilot-msg mb-2 p-2 rounded small${m.mine ? " bg-success-subtle ms-4" : " bg-body-tertiary me-4"}`}
            >
              <span className="visually-hidden">{m.mine ? "Você: " : "CX ERP: "}</span>
              {m.text}
            </div>
          ))}
        </div>
        <form
          className="d-flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) send.mutate(text.trim());
          }}
        >
          <label htmlFor="wa-sim" className="visually-hidden">
            Mensagem do seu WhatsApp
          </label>
          <input
            id="wa-sim"
            className="form-control"
            maxLength={1000}
            placeholder="Mensagem do seu WhatsApp"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" className="btn btn-success" disabled={send.isPending || !text.trim()}>
            Enviar
          </button>
        </form>
      </div>
    </div>
  );
}
